// client/src/coms/views/CanvasStandaloneView.js
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoginButton from '../components/LoginButton';
import CanvasView from './canvas_parts/CanvasView';
import CanvasRoomView from './canvas_parts/CanvasRoomView';
import '../coms.css';

const GUEST_CANVAS_ID = 2147483646;
const GUEST_CANVAS_QUERY_ID = 'guest';

function getUserId(user) {
  const n = Number(user && (user.id != null ? user.id : user.user_id));
  return Number.isInteger(n) && n > 0 ? n : 0;
}

const pageStyle = {
  minHeight: '100vh',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'var(--vxs-2, #17191f)',
  color: 'var(--vx-text, #fff)',
};

const headerStyle = {
  height: 56,
  minHeight: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '0 14px',
  boxSizing: 'border-box',
  borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
  background: 'var(--vxs-4, #202228)',
  color: 'var(--vx-text, #fff)',
  zIndex: 20,
};

const titleWrapStyle = {
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const backButtonStyle = {
  width: 34,
  height: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.16 * var(--vx-ov, 1)))',
  borderRadius: 7,
  background: 'var(--vxs-5, #2b2e35)',
  color: 'var(--vx-text, #fff)',
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
};

const titleStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 16,
  fontWeight: 800,
};

const guestStatusStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.68 * var(--vx-ov, 1)))',
  fontSize: 12,
  fontWeight: 600,
};

const loginButtonStyle = {
  minHeight: 36,
  flex: '0 0 auto',
  padding: '0 15px',
  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.2 * var(--vx-ov, 1)))',
  borderRadius: 7,
  background: 'var(--vxs-6, #343944)',
  color: 'var(--vx-text, #fff)',
  fontWeight: 750,
  cursor: 'pointer',
};

const canvasWrapStyle = {
  minHeight: 0,
  flex: '1 1 auto',
  overflow: 'hidden',
};

const libraryStyle = {
  minHeight: 0,
  flex: '1 1 auto',
  overflowY: 'auto',
  padding: 24,
  boxSizing: 'border-box',
};

const cardStyle = {
  width: 'min(420px, 100%)',
  overflow: 'hidden',
  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
  borderRadius: 18,
  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))',
  boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
};

const openButtonStyle = {
  width: '100%',
  display: 'block',
  padding: 0,
  border: 0,
  background: 'transparent',
  color: 'var(--vx-text, #fff)',
  textAlign: 'left',
  cursor: 'pointer',
};

function GuestCanvasLibrary({ onOpen }) {
  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div style={titleWrapStyle}>
          <div>
            <div style={titleStyle}>Voxemera Canvas</div>
            <div style={guestStatusStyle}>Guest mode · canvases are saved only on this device</div>
          </div>
        </div>

        <LoginButton redirectPath="/canvas" style={loginButtonStyle}>
          Log in with Google
        </LoginButton>
      </header>

      <main style={libraryStyle}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 30, fontWeight: 900, marginBottom: 8 }}>Canvas</div>
            <div style={{ color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.68 * var(--vx-ov, 1)))', fontSize: 14, lineHeight: 1.5 }}>
              Draw without an account using your local guest canvas.
            </div>
          </div>

          <section
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: 24,
              alignItems: 'center',
              marginBottom: 26,
              padding: 24,
              border: '1px solid rgba(var(--vx-accent-rgb, 99,102,241), 0.34)',
              borderRadius: 18,
              background:
                'linear-gradient(135deg, rgba(99,102,241,0.17), rgba(6,182,212,0.09))',
              boxShadow: '0 18px 50px rgba(0,0,0,0.20)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 21, fontWeight: 900, marginBottom: 8 }}>
                Create together with Voxemera
              </div>
              <div
                style={{
                  maxWidth: 760,
                  color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.76 * var(--vx-ov, 1)))',
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                Sign in to create collaborative canvases, invite other artists and draw together
                live. Your account also lets you open your canvases from your other devices.
              </div>
              <div
                style={{
                  marginTop: 10,
                  color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.52 * var(--vx-ov, 1)))',
                  fontSize: 12,
                  lineHeight: 1.5,
                  fontWeight: 650,
                }}
              >
                Guest canvases stay on this device and cannot be shared for live collaboration.
              </div>
            </div>

            <LoginButton
              redirectPath="/canvas"
              style={{
                ...loginButtonStyle,
                minHeight: 42,
                padding: '0 18px',
                background: 'var(--vx-accent, #5865f2)',
                borderColor: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.22 * var(--vx-ov, 1)))',
                whiteSpace: 'nowrap',
              }}
            >
              Continue with Google
            </LoginButton>
          </section>

          <div style={cardStyle}>
            <button type="button" style={openButtonStyle} onClick={onOpen}>
              <div
                style={{
                  height: 170,
                  display: 'flex',
                  alignItems: 'flex-end',
                  padding: 18,
                  boxSizing: 'border-box',
                  background: 'linear-gradient(135deg, rgba(var(--vx-accent-rgb, 99,102,241), 0.95), rgba(6,182,212,0.82))',
                }}
              >
                <div
                  style={{
                    width: 58,
                    height: 58,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 16,
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.24 * var(--vx-ov, 1)))',
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.16 * var(--vx-ov, 1)))',
                    fontSize: 20,
                    fontWeight: 900,
                  }}
                >
                  GC
                </div>
              </div>

              <div style={{ padding: 18 }}>
                <div style={{ fontSize: 19, fontWeight: 900, marginBottom: 8 }}>Guest Canvas</div>
                <div style={{ color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.64 * var(--vx-ov, 1)))', fontSize: 13, lineHeight: 1.5 }}>
                  Single-device canvas stored locally in this browser.
                </div>
              </div>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function CanvasStandaloneView({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUserId = useMemo(() => getUserId(user), [user]);
  const selectedCanvas = String(searchParams.get('canvasId') || '').trim().toLowerCase();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Voxemera';

    return () => {
      document.title = previousTitle;
    };
  }, []);

  if (currentUserId > 0) {
    return <CanvasView user={user} />;
  }

  if (selectedCanvas !== GUEST_CANVAS_QUERY_ID) {
    return <GuestCanvasLibrary onOpen={() => navigate('/canvas?canvasId=guest')} />;
  }

  const guestCanvas = {
    id: GUEST_CANVAS_ID,
    name: 'Guest Canvas',
    owner_user_id: 0,
    participant_user_ids: [],
    max_participants: 1,
    canvas_width: 1920,
    canvas_height: 1080,
    animation_enabled: false,
  };

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div style={titleWrapStyle}>
          <button
            type="button"
            aria-label="Back to canvases"
            title="Back to canvases"
            style={backButtonStyle}
            onClick={() => navigate('/canvas', { replace: false })}
          >
            ←
          </button>

          <div style={{ minWidth: 0 }}>
            <div style={titleStyle}>Guest Canvas</div>
            <div style={guestStatusStyle}>Saved only on this device</div>
          </div>
        </div>

        <LoginButton redirectPath="/canvas" style={loginButtonStyle}>
          Log in with Google
        </LoginButton>
      </header>

      <div style={canvasWrapStyle}>
        <CanvasRoomView
          canvasId={GUEST_CANVAS_ID}
          canvas={guestCanvas}
          user={null}
          currentUser={{ username: 'Guest' }}
          currentUserName="Guest"
          onBack={() => navigate('/canvas', { replace: false })}
        />
      </div>
    </div>
  );
}
